import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { FiSearch, FiFilter, FiChevronLeft, FiChevronRight, FiMessageCircle } from 'react-icons/fi';

const TYPE_LABELS = {
  whatsapp: 'WhatsApp', whatsapp_image: 'WA Image', whatsapp_video: 'WA Video',
  whatsapp_document: 'WA Doc', sms: 'SMS', email: 'Email', rcs: 'RCS',
  newsletter: 'Newsletter', push_notification: 'Push', voice_call: 'Voice',
  status_messaging: 'Status', custom: 'Custom'
};
const TYPE_COLORS = {
  sms: '#7ea52e', whatsapp: '#7ea52e', whatsapp_image: '#6b8f27', whatsapp_video: '#57721f',
  whatsapp_document: '#455a19', rcs: '#b7d083', newsletter: '#5c7a1f',
  push_notification: '#7e7e71', voice_call: '#abab9f', status_messaging: '#161614',
  email: '#1a1a18', custom: '#c9c9c3'
};

export default function MyCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState('newest');
  const navigate = useNavigate();

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, sort });
      if (search) params.set('search', search);
      if (typeFilter) params.set('type', typeFilter);
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await api.get(`/user/campaigns?${params}`);
      const list = Array.isArray(data.campaigns) ? data.campaigns : [];
      setCampaigns(list);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch {
      setCampaigns([]);
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [page, search, typeFilter, statusFilter, sort]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const clearFilters = () => {
    setSearch(''); setSearchInput(''); setTypeFilter(''); setStatusFilter(''); setSort('newest'); setPage(1);
  };

  const hasFilters = search || typeFilter || statusFilter || sort !== 'newest';

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">My Broadcasts</h1>
          <p className="page-subtitle">
            {total > 0 ? `${total} campaign${total !== 1 ? 's' : ''} assigned to you` : 'Campaigns assigned to you'}
          </p>
        </div>
        {total > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 18px', borderRadius: 12, background: 'linear-gradient(135deg, #eef3e6, #e5edda)' }}>
            <FiMessageCircle size={14} color="#7ea52e" />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#455a19' }}>{total} Total</span>
          </div>
        )}
      </div>

      {/* Search & Filter Bar */}
      <div className="card" style={{ marginBottom: 20, padding: '16px 20px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <form onSubmit={handleSearch} style={{ flex: 1, minWidth: 200, position: 'relative' }}>
            <FiSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#abab9f' }} />
            <input type="text" className="form-input" placeholder="Search campaigns..."
              value={searchInput} onChange={e => setSearchInput(e.target.value)}
              style={{ paddingLeft: 36, width: '100%' }} />
          </form>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiFilter size={14} color="#abab9f" />
            <select className="form-input" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto', fontSize: 13, padding: '8px 12px', minWidth: 120 }}>
              <option value="">All Types</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="form-input" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              style={{ width: 'auto', fontSize: 13, padding: '8px 12px', minWidth: 120 }}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="paused">Paused</option>
              <option value="draft">Draft</option>
            </select>
            <select className="form-input" value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}
              style={{ width: 'auto', fontSize: 13, padding: '8px 12px', minWidth: 110 }}>
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="name">Name A-Z</option>
            </select>
            {hasFilters && (
              <button className="btn btn-secondary" onClick={clearFilters} style={{ fontSize: 12, padding: '8px 14px', whiteSpace: 'nowrap' }}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner">
          <div className="spinner" />
          <div className="loading-text">Loading...</div>
          <div className="loading-dots"><span /><span /><span /></div>
        </div>
      ) : campaigns.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <FiMessageCircle size={40} style={{ color: '#abab9f', marginBottom: 12 }} />
          <h3 style={{ color: 'var(--text-primary)', marginBottom: 6 }}>No campaigns found</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {hasFilters ? 'Try adjusting your filters' : 'No campaigns assigned to you yet'}
          </p>
        </div>
      ) : (
        <>
          <div className="campaign-list">
            {campaigns.map((c, i) => {
              const progress = parseFloat(c.progress) || 0;
              const progressColor = progress >= 80 ? '#7ea52e' : progress >= 50 ? '#9ec25a' : '#b7d083';
              const typeColor = TYPE_COLORS[c.type] || '#7ea52e';
              const typeLabel = TYPE_LABELS[c.type] || c.type?.replace('_', ' ') || 'Campaign';
              const typeShort = typeLabel.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
              return (
                <div key={c._id} className="campaign-row" onClick={() => navigate(`/user/campaigns/${c._id}`)}
                  style={{ animationDelay: `${i * 0.04}s` }}>
                  <div className="campaign-accent" style={{ background: typeColor }} />

                  <div className="campaign-icon" style={{ background: `${typeColor}1a`, color: typeColor }}>
                    {typeShort}
                  </div>

                  <div className="campaign-main">
                    <div className="campaign-top">
                      <div className="campaign-title">
                        <h3>{c.name}</h3>
                        <span className="campaign-type" style={{ background: typeColor }}>
                          {typeLabel}
                        </span>
                        <span className={`badge badge-${c.status}`}>{c.status}</span>
                      </div>
                      <div className="campaign-date">{new Date(c.createdAt).toLocaleDateString()}</div>
                    </div>

                    <div className="campaign-stats">
                      {[
                        { label: 'Assigned', value: c.myAssigned || 0, color: '#7ea52e' },
                        { label: 'Sent', value: c.mySent || 0, color: '#7ea52e' },
                        { label: 'Failed', value: c.myFailed || 0, color: '#1a1a18' },
                        { label: 'Pending', value: c.myPending || 0, color: '#9ec25a' },
                      ].map((s, idx) => (
                        <div key={idx} className="campaign-stat">
                          <div className="campaign-stat-value" style={{ color: s.color }}>{s.value.toLocaleString()}</div>
                          <div className="campaign-stat-label">{s.label}</div>
                        </div>
                      ))}
                    </div>

                    <div className="campaign-progress">
                      <div className="campaign-progress-meta">
                        <span>Progress</span>
                        <span style={{ color: progressColor, fontWeight: 800 }}>{progress}%</span>
                      </div>
                      <div className="campaign-progress-bar">
                        <div className="campaign-progress-fill" style={{
                          width: `${Math.min(progress, 100)}%`,
                          background: `linear-gradient(90deg, ${progressColor}, ${progressColor}cc)`
                        }} />
                      </div>
                    </div>
                  </div>

                  <div className="campaign-side">
                    <div className="campaign-kpi">
                      <span className="campaign-kpi-label">Assigned</span>
                      <span className="campaign-kpi-value">{(c.myAssigned || 0).toLocaleString()}</span>
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/user/campaigns/${c._id}`);
                    }}>
                      View
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 24 }}>
              <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                <FiChevronLeft size={14} /> Prev
              </button>
              <div style={{ display: 'flex', gap: 4 }}>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button key={p} className={`btn ${p === page ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ minWidth: 36, padding: '6px 0', fontSize: 13 }} onClick={() => setPage(p)}>
                      {p}
                    </button>
                  );
                })}
              </div>
              <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                Next <FiChevronRight size={14} />
              </button>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>
                Page {page} of {totalPages}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}
